import { useEffect, useContext, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { Result, Spin, BackTop } from "antd";
import { ArrowUpOutlined } from "@ant-design/icons";

import ThemeProvider from "./utils/context/Theme/ThemeProvider";
import TestTypeProvider from "./utils/context/TestType/TestTypeProvider";
import PlayAudioVisibilityProvider from "./utils/context/PlayAudioVisibility/PlayAudioVisibilityProvider";
import ProtectedRoute from "./utils/hooks/Auth/ProtectedRoute";
import { getDataLocalStorage } from "./utils/helpers/localStorageHelper";

import DashboardLayout from "./pages/DashboardAll/Dashboard/DashboardLayout";
import ThemeContext from "./utils/context/Theme/ThemeContext";

import "./App.css";
import "./utils/helpers/animation/animate.css";

/* ===================== Lazy Pages ===================== */
/* Public */
const Home = lazy(() => import("./pages/Home"));
const LoginForm = lazy(() => import("./pages/Auth/LoginForm"));
const RegisterForm = lazy(() => import("./pages/Auth/RegisterForm"));
const ForgotPasswordForm = lazy(
  () => import("./pages/Auth/ForgotPasswordForm"),
);
const SendVerCodeForm = lazy(() => import("./pages/Auth/SendVerCodeForm"));
const UpdatePasswordForm = lazy(
  () => import("./pages/Auth/UpdatePasswordForm"),
);
const NotFound = lazy(() => import("./pages/Common/NotFound"));

/* Student Dashboard */
const StudentHome = lazy(() => import("./pages/DashboardAll/StudentHome"));
const DevelopingEnLangSkills = lazy(
  () => import("./pages/DashboardAll/DevelopingEnLangSkills"),
);
const InteractiveListeningAndReading = lazy(
  () =>
    import("./pages/DashboardAll/DevelopingEnLangSkills/InteractiveListeningAndReading"),
);
const PlacementTest = lazy(() => import("./pages/DashboardAll/PlacementTest"));
const IELTSLessons = lazy(() => import("./pages/DashboardAll/IELTSLessons"));
const DevelopingIeltsSkills = lazy(
  () => import("./pages/DashboardAll/DevelopingIeltsSkills"),
);
const QueTypeTrainingFullSec = lazy(
  () => import("./pages/DashboardAll/QueTypeTraining&FullSec"),
);
const WriteSpeakCorrection = lazy(
  () => import("./pages/DashboardAll/WriteSpeakCorrection"),
);
const RealisticTests = lazy(
  () => import("./pages/DashboardAll/RealisticTests"),
);
const Library = lazy(() => import("./pages/DashboardAll/Library"));
const Support = lazy(() => import("./pages/DashboardAll/Support"));
const Revision = lazy(() => import("./pages/Revisions"));

/* Teacher Dashboard */
const TeacherHome = lazy(() => import("./pages/DashboardAll/TeacherHome"));
const SubmissionsInbox = lazy(
  () => import("./pages/DashboardAll/SubmissionsInbox"),
);
const TeacWriteSpeakCorrection = lazy(
  () => import("./pages/DashboardAll/TeacWriteSpeakCorrection"),
);
const StartCorrection = lazy(
  () => import("./pages/DashboardAll/TeacWriteSpeakCorrection/StartCorrection"),
);
const Balance = lazy(() => import("./pages/DashboardAll/Balance"));
const Settings = lazy(() => import("./pages/DashboardAll/Settings"));

/* Tests */
const PreTestInstructions = lazy(
  () => import("./pages/Tests/PreTestInstructions"),
);
const ListeningTest = lazy(() => import("./pages/Tests/ListeningTest"));
const ReadingTest = lazy(() => import("./pages/Tests/ReadingTest"));
const WritingTest = lazy(() => import("./pages/Tests/WritingTest"));
const SpeakingTest = lazy(() => import("./pages/Tests/SpeakingTest"));
const MicTest = lazy(() => import("./pages/Tests/SpeakingTest/MicTest"));

const DashboardRouter = () => {
  const role = getDataLocalStorage("role");

  if (role === "instructor") return <TeacherHome />;
  return <StudentHome />;
};

const App = () => {
  const { themeMode } = useContext(ThemeContext);

  useEffect(() => {
    if (themeMode === "dark") {
      document.body.classList.add("darkBody");
    } else {
      document.body.classList.remove("darkBody");
    }
  }, [themeMode]);

  return (
    <div
      className="App"
      onCopy={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
    >
      <BackTop duration={1000}>
        <div className="text-white">
          <ArrowUpOutlined />
        </div>
      </BackTop>
      <Home />
    </div>
  );
};

const MainApp = () => {
  return (
    <ThemeProvider>
      <TestTypeProvider>
        <PlayAudioVisibilityProvider>
          <Suspense fallback={<Spin size="large" />}>
            <ErrorBoundary
              fallback={<Result subTitle="Sorry, something went wrong." />}
            >
              <Router>
                <Routes>
                  {/* ========== Public ========== */}
                  <Route path="/" element={<App />} />
                  <Route path="/login" element={<LoginForm />} />
                  <Route path="/register" element={<RegisterForm />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordForm />}
                  />
                  <Route
                    path="/reset-password"
                    element={<SendVerCodeForm forResetPass />}
                  />
                  <Route
                    path="/update-password"
                    element={<UpdatePasswordForm />}
                  />
                  <Route path="/verify-email" element={<SendVerCodeForm />} />

                  {/* ========== Dashboard (Layout Route) ========== */}
                  <Route
                    path="/dashboard"
                    element={
                      // <ProtectedRoute>
                        <DashboardLayout />
                      //</ProtectedRoute>
                    } 
                  >
                    {/* shared */}
                    <Route index element={<DashboardRouter />} />

                    {/* student */}
                    <Route
                      path="developing-enLang-skills"
                      element={<DevelopingEnLangSkills />}
                    />

                    <Route path="placement-test" element={<PlacementTest />} />
                    <Route path="ielts-lessons" element={<IELTSLessons />} />
                    <Route
                      path="developing-ielts-skills"
                      element={<DevelopingIeltsSkills />}
                    />
                    <Route
                      path="std-writing-correction"
                      element={<WriteSpeakCorrection serviceType="writing" />}
                    />
                    <Route
                      path="std-speaking-correction"
                      element={<WriteSpeakCorrection serviceType="speaking" />}
                    />
                    <Route
                      path="queTypeTraining-fullSec"
                      element={<QueTypeTrainingFullSec />}
                    />
                    <Route
                      path="realistic-tests"
                      element={<RealisticTests />}
                    />
                    <Route path="library" element={<Library />} />
                    <Route path="support" element={<Support />} />
                    <Route path="revision" element={<Revision />} />

                    {/* teacher */}
                    <Route
                      path="submissions-inbox"
                      element={<SubmissionsInbox />}
                    />
                    <Route path="balance" element={<Balance />} />
                    <Route path="settings" element={<Settings />} />
                    <Route
                      path="teac-writing-correction"
                      element={<TeacWriteSpeakCorrection corrFor="writing" />}
                    />
                    <Route
                      path="teac-speaking-correction"
                      element={<TeacWriteSpeakCorrection corrFor="speaking" />}
                    />
                  </Route>

                  <Route
                    path="/developing-enLang-skills/interactive-section"
                    element={<InteractiveListeningAndReading />}
                  />

                  <Route
                    path="start-correction"
                    element={<StartCorrection />}
                  />
                  {/* ========== Tests ========== */}
                  <Route
                    path="/pre-test-instructions"
                    element={
                      <ProtectedRoute>
                        <PreTestInstructions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/listening-test"
                    element={
                      <ProtectedRoute>
                        <ListeningTest />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reading-test"
                    element={
                      <ProtectedRoute>
                        <ReadingTest />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/writing-test"
                    element={
                      <ProtectedRoute>
                        <WritingTest />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/speaking-test"
                    element={
                      <ProtectedRoute>
                        <SpeakingTest />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/speaking-test/mic-test"
                    element={
                      <ProtectedRoute>
                        <MicTest />
                      </ProtectedRoute>
                    }
                  />

                  {/* ========== 404 ========== */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Router>
            </ErrorBoundary>
          </Suspense>
        </PlayAudioVisibilityProvider>
      </TestTypeProvider>
    </ThemeProvider>
  );
};

export default MainApp;
